// @flow

import React from 'react';
import Relay from 'react-relay/classic';
import { second, seconds } from 'metrick/duration';
import throttle from 'throttleit';

import Dropdown from 'app/components/shared/Dropdown';
import Emojify from 'app/components/shared/Emojify';
import Panel from 'app/components/shared/Panel';
import SearchField from 'app/components/shared/SearchField';
import ShowMoreFooter from 'app/components/shared/ShowMoreFooter';
import Spinner from 'app/components/shared/Spinner';
import { formatNumber } from 'app/lib/number';

import CableStore from 'app/stores/CableStore';

import AgentRow from './row';

const PAGE_SIZE = 100;

const OPTION_KINDS = {
  NONE: 'none',
  CLUSTER: 'cluster',
  QUEUE: 'queue'
};

type ClusterQueueNode = {
  id: string,
  key: string
};

type ClusterNode = {
  id: string,
  name: string,
  queues: {
    edges: Array<{
      node: ClusterQueueNode
    }>
  }
};

type Props = {
  organization: {
    clusters: ?{
      edges: Array<{
        node: ClusterNode
      }>
    },
    allAgents: ?{
      count: number
    },
    agents: ?{
      count: number,
      edges: Array<Object>
    }
  },
  location: {
    query: {
      [string]: ?string
    }
  },
  relay: Object
};

type State = {
  loading: boolean,
  searchingRemotely: boolean,
  searchingRemotelyIsSlow: boolean,
  localSearchQuery: ?Array<string>,
  defaultQuery: ?string
};

class Agents extends React.PureComponent<Props, State> {
  clusterFilterDropdown: ?Dropdown;
  agentListRefreshTimeout: TimeoutID;
  remoteSearchIsSlowTimeout: TimeoutID;

  // For some reason we need to explicitly type this so the assignment of
  // defaultQuery in the constructor doesn't give us a Flow error. Weird.
  state: State = {
    loading: false,
    searchingRemotely: false,
    searchingRemotelyIsSlow: false,
    localSearchQuery: null,
    defaultQuery: null
  };

  constructor(initialProps) {
    super(initialProps);

    this.state.defaultQuery = this.props.location.query.q;
  }

  componentDidMount() {
    // We've marked the data requirements for this compoent with `@include(if:
    // $isMounted)` which allows us to defer the actual loading of the data
    // until we're ready. This keeps the page speedy to load and we can load
    // the Agents in after this component has mounted.
    //
    // We use `forceFetch` instead of `setVariables` because we want to re-grab
    // the entire agent list when we come to/from the Agents page whether it's
    // via react-router or the back/forward button in the browser.
    this.props.relay.forceFetch({ search: this.state.defaultQuery, isMounted: true }, (readyState) => {
      if (readyState.done) {
        CableStore.on('organization_stats:change', this.fetchUpdatedData);
        this.startTimeout();
      }
    });
  }

  componentWillUnmount() {
    CableStore.off('organization_stats:change', this.fetchUpdatedData);
    clearTimeout(this.agentListRefreshTimeout);
  }

  // We refresh the data under 2 circumstances. The refresh happens on what
  // ever happens first.
  //
  // 1. We receive an `organization_stats:change` push event
  // 2. 10 seconds have passed
  startTimeout = () => {
    this.agentListRefreshTimeout = setTimeout(
      this.fetchUpdatedData,
      seconds.bind(10)
    );
  };

  // Throttle the `fetchUpdatedData` function so we don't ever reload the
  // entire list more than once every 3 seconds
  fetchUpdatedData = throttle(
    () => {
      this.props.relay.forceFetch(
        true,
        (readyState) => {
          if (readyState.done) {
            // Start a timeout that will cause the data to be refreshed again
            // in a few seconds time
            this.startTimeout();
          }
        }
      );
    },
    seconds.bind(3)
  );

  render() {
    // grab (potentially) filtered agent list here, as we need it in several places
    const agents = this.getRelevantAgents();

    return (
      <Panel>
        <Panel.Row>
          <div className="flex">
            {this.props.organization.clusters && this.props.organization.clusters.edges.length > 0 && (
              <Dropdown
                width={250}
                className="flex items-center mr3"
                ref={(clusterFilterDropdown) => this.clusterFilterDropdown = clusterFilterDropdown}
              >
                {this.renderSelectedClusterFilter()}
                {this.renderClusterFilters()}
              </Dropdown>
            )}
            <SearchField
              placeholder="Filter agents…"
              className="flex-auto"
              onChange={this.handleSearch}
              searching={this.state.searchingRemotelyIsSlow}
              defaultValue={this.state.defaultQuery}
            />
          </div>
        </Panel.Row>
        {this.renderSearchInfo(agents)}
        {this.renderAgentList(agents)}
        <ShowMoreFooter
          connection={this.props.organization.agents}
          label="agents"
          loading={this.state.loading}
          searching={!!this.state.localSearchQuery}
          onShowMore={this.handleShowMoreAgents}
        />
      </Panel>
    );
  }

  renderSelectedClusterFilter() {
    const { cluster: clusterFilter, clusterQueue: clusterQueueFilter } = this.props.relay.variables;

    let filterName = 'All Agents';

    if (clusterFilter && this.props.organization.clusters) {
      const selectedClusterFilter = this.props.organization.clusters.edges.find(({ node: cluster }) => cluster.id === clusterFilter);

      if (selectedClusterFilter) {
        filterName = (
          <React.Fragment>
            Any Queue in <Emojify text={selectedClusterFilter.node.name} />
          </React.Fragment>
        );
      }
    } else if (clusterQueueFilter && this.props.organization.clusters) {
      let selectedClusterQueueFilter: ?{ node: ClusterQueueNode };

      const selectedClusterFilter = this.props.organization.clusters.edges.find(({ node: cluster }) => {
        selectedClusterQueueFilter = cluster.queues.edges.find(({ node: queue }) => queue.id === clusterQueueFilter[0]);

        return selectedClusterQueueFilter !== undefined;
      });

      if (!selectedClusterQueueFilter) {
        throw new TypeError('Cluster for selectedClusterQueueFilter not found');
      }

      filterName = (
        <React.Fragment>
          <span className="monospace">
            {selectedClusterQueueFilter?.node.key || 'A Queue'}
          </span>
          {' in '}
          <Emojify text={selectedClusterFilter?.node.name || 'A Cluster'} />
        </React.Fragment>
      );
    }

    return (
      <div
        className="underline-dotted cursor-pointer inline-block regular dark-gray truncate"
        style={{ maxWidth: 200 }}
      >
        {filterName}
      </div>
    );
  }

  renderClusterFilters() {
    return (
      <React.Fragment>
        <div
          value=""
          className="btn block hover-bg-silver"
          data-kind={OPTION_KINDS.NONE}
          onClick={this.handleClusterFilter}
        >
          All Agents
        </div>
        {this.props.organization.clusters && this.props.organization.clusters.edges.map(({ node: cluster }) => (
          <React.Fragment key={cluster.id}>
            <div className="py1 px3 flex items-center dark-gray">
              <h4 className="m0 mr1 text-uppercase">
                <Emojify text={cluster.name} />
              </h4>
              <hr
                className="flex-auto"
                style={{
                  border: 'none',
                  borderBottom: '1px solid currentColor'
                }}
              />
            </div>
            <div
              className="btn block hover-bg-silver"
              data-kind={OPTION_KINDS.CLUSTER}
              data-cluster-id={cluster.id}
              onClick={this.handleClusterFilter}
            >
              Any Queue in <Emojify text={cluster.name} />
            </div>
            {cluster.queues.edges.map(({ node: queue }) => (
              <div
                key={queue.id}
                className="btn block hover-bg-silver monospace"
                data-kind={OPTION_KINDS.QUEUE}
                data-queue-id={queue.id}
                onClick={this.handleClusterFilter}
              >
                {queue.key}
              </div>
            ))}
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  }

  getRelevantAgents() {
    const { organization: { agents: organizationAgents } } = this.props;

    if (!organizationAgents) {
      return null;
    }

    const { edges: agents } = organizationAgents;
    const { localSearchQuery } = this.state;

    if (!localSearchQuery) {
      return agents;
    }

    // Parse the search. We only want to do this once, outside of the agent
    // loop, in case they have lots of agents
    const queries = localSearchQuery.map((string) => {
      const [key, value] = string.split('=');

      return {
        string: string.toLowerCase(),
        metaDataKey: key,
        metaDataValue: value // may be undefined
      };
    });

    const anyQueryMatchesName = (agent) => {
      const lowercaseName = agent.name.toLowerCase();

      return queries.some((query) => lowercaseName.indexOf(query.string) !== -1);
    };

    const anyQueryMatchesMetaData = (agent) => {
      return agent.metaData.some((metaDataKeyValue) => {
        const [key, value] = metaDataKeyValue.split('=');

        return queries.some((query) => {
          // Simple string match
          //
          // 'mo' matches 'moo=true'
          // 'tru' matches 'moo=true'
          // 'bark' does not match 'moo=true'
          if (!query.metaDataValue && key.indexOf(query.metaDataKey) !== -1 || value.indexOf(query.metaDataKey) !== -1) {
            return true;
          }

          // Wildcard matching
          //
          // 'moo=*' matches 'moo=true'
          // 'moo=*' doesn't match 'bark=true'
          if (query.metaDataKey === key && query.metaDataValue === '*') {
            return true;
          }

          // Key=Value matching
          //
          // 'moo=tr' matches 'moo=true'
          // 'moo=true' matches 'moo=true'
          // 'moo=rue' doesn't match 'moo=true'
          // 'moo=false' doesn't match 'moo=true'
          if (query.metaDataKey === key && value.indexOf(query.metaDataValue) === 0) {
            return true;
          }
        });
      });
    };

    return agents.filter(({ node: agent }) => {
      return anyQueryMatchesName(agent) || anyQueryMatchesMetaData(agent);
    });
  }

  renderSearchInfo(relevantAgents) {
    const { organization: { agents }, relay: { variables: { search: remoteSearchQuery } } } = this.props;
    const { localSearchQuery } = this.state;

    if ((localSearchQuery && relevantAgents) || remoteSearchQuery && agents) {
      return (
        <div className="bg-silver semi-bold py2 px3">
          <small className="dark-gray">
            {formatNumber(localSearchQuery ? (relevantAgents ? relevantAgents.length : 0) : (agents ? agents.count : 0))} matching agents
          </small>
        </div>
      );
    }
  }

  renderAgentList(relevantAgents) {
    const { relay: { variables: { search: remoteSearchQuery } } } = this.props;
    const { localSearchQuery } = this.state;

    const isContextFiltered = !!(remoteSearchQuery || localSearchQuery);

    if (!relevantAgents) {
      return (
        <Panel.Section className="center">
          <Spinner />
        </Panel.Section>
      );
    } else if (relevantAgents.length > 0) {
      return relevantAgents.map(({ node: agent }) => <AgentRow key={agent.id} agent={agent} />);
    } else if (!isContextFiltered) {
      return (
        <Panel.Section className="dark-gray">
          No agents connected
        </Panel.Section>
      );
    }
  }

  get useLocalSearch() {
    return this.props.organization.agents &&
      (this.props.organization.allAgents && this.props.organization.allAgents.count <= PAGE_SIZE) &&
      this.props.relay.variables.cluster === null &&
      this.props.relay.variables.clusterQueue === null;
  }

  get useRemoteSearch() {
    return !this.useLocalSearch;
  }

  handleSearch = (query) => {
    if (this.useLocalSearch) {
      return this.handleLocalSearch(query);
    }

    return this.handleRemoteSearch(query);
  };

  handleClusterFilter = (event) => {
    this.clusterFilterDropdown && this.clusterFilterDropdown.setShowing(false);

    const selectedOptionData = event.currentTarget.dataset;
    const newVariables: { cluster: ?string, clusterQueue: ?Array<string> } = {
      cluster: null,
      clusterQueue: null
    };

    switch (selectedOptionData.kind) {
      case OPTION_KINDS.NONE:
        // Let both the variables be null!
        break;

      case OPTION_KINDS.CLUSTER:
        newVariables.cluster = selectedOptionData.clusterId;
        break;

      case OPTION_KINDS.QUEUE:
        newVariables.clusterQueue = [selectedOptionData.queueId];
        break;

      default:
        throw new TypeError('Unknown cluster kind passed to handleClusterFilter');
    }

    this.setState({ searchingRemotely: true }, () => {
      this.props.relay.setVariables(newVariables, (readyState) => {
        if (readyState.done) {
          this.setState({ searchingRemotely: false });
        }
      });
    });
  };

  handleLocalSearch = (query) => {
    // if there's a remote search active we make doubly sure to
    // reset it this shouldn't happen but stranger things have!
    const shouldResetSearch = !!this.props.relay.variables.search;

    // remove leading and trailing whitespace
    query = query.trim();

    const newState = Object.assign({}, this.state, {
      localSearchQuery: query ? query.split(/\s+/g) : null
    });

    if (shouldResetSearch) {
      newState.searchingRemotely = true;
    }

    this.setState(newState);

    if (shouldResetSearch) {
      this.props.relay.setVariables(
        {
          search: null
        },
        (readyState) => {
          if (readyState.done) {
            this.setState({ searchingRemotely: false });
          }
        }
      );
    }
  };

  handleRemoteSearch = (query) => {
    this.setState({ localSearchQuery: null, searchingRemotely: true });

    if (this.remoteSearchIsSlowTimeout) {
      clearTimeout(this.remoteSearchIsSlowTimeout);
    }

    this.remoteSearchIsSlowTimeout = setTimeout(() => {
      this.setState({ searchingRemotelyIsSlow: true });
    }, second.bind(1));

    this.props.relay.forceFetch(
      {
        search: query
      },
      (readyState) => {
        if (readyState.done) {
          if (this.remoteSearchIsSlowTimeout) {
            clearTimeout(this.remoteSearchIsSlowTimeout);
          }
          this.setState({
            searchingRemotely: false,
            searchingRemotelyIsSlow: false
          });
        }
      }
    );
  };

  handleShowMoreAgents = () => {
    this.setState({ loading: true });

    this.props.relay.setVariables(
      {
        pageSize: this.props.relay.variables.pageSize + PAGE_SIZE
      },
      (readyState) => {
        if (readyState.done) {
          this.setState({ loading: false });
        }
      }
    );
  };
}

export default Relay.createContainer(Agents, {
  initialVariables: {
    includeClusters: Features.Clusters,
    isMounted: false,
    search: null,
    cluster: null,
    clusterQueue: null,
    pageSize: PAGE_SIZE
  },

  fragments: {
    organization: () => Relay.QL`
      fragment on Organization {
        clusters(first: 500) @include(if: $includeClusters) {
          edges {
            node {
              id
              name
              queues(first: 500) {
                edges {
                  node {
                    id
                    key
                  }
                }
              }
            }
          }
        }
        allAgents: agents @include(if: $isMounted) {
          count
        }
        agents(first: $pageSize, search: $search, cluster: $cluster, clusterQueue: $clusterQueue) @include(if: $isMounted) {
          ${ShowMoreFooter.getFragment('connection')}
          count
          edges {
            node {
              id
              metaData
              name
              ${AgentRow.getFragment('agent')}
            }
          }
        }
      }
    `
  }
});
